/**
 * P4-N5: 穿戴设备管理面板 — 设备列表 + 模拟输入 + 指令日志
 * 用于 RuntimeLogPanel 的「设备」Tab
 */
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../services/apiClient';
import { Watch, Plus, Trash2, Radio, Send } from 'lucide-react';

interface WearableDevice {
  id: string;
  name: string;
  type: string;
  platform: string;
  status: string;
  capabilities: string[];
  bindCode: string;
  lastSeen: string;
}

export function WearableDevicePanel({ t }: { t: any }) {
  const [devices, setDevices] = useState<WearableDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [simVoice, setSimVoice] = useState('');
  const [simResult, setSimResult] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/wearable/devices', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setDevices(data.devices || []);
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleBind = useCallback(async () => {
    setMsg('');
    try {
      const res = await apiFetch('/api/wearable/bind', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: '模拟眼镜', type: 'glasses', platform: 'qianwen_s1', capabilities: ['voice', 'camera', 'gps', 'display'] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '绑定失败');
      setMsg(`设备已绑定，绑定码: ${data.device?.bindCode}`);
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [load]);

  const handleUnbind = useCallback(async (id: string) => {
    try {
      // Bug 18: 添加 res.ok 检查
      const res = await apiFetch(`/api/wearable/devices/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '解绑失败');
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [load]);

  const handleSimulate = useCallback(async () => {
    setMsg(''); setSimResult(null);
    try {
      const res = await apiFetch('/api/wearable/simulate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: devices[0]?.id || 'sim', voice: simVoice || '你好，今天天气怎么样？', location: { lat: 30.27, lng: 120.15 } }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '模拟失败');
      setSimResult(data);
      setMsg('模拟输入已发送');
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [devices, simVoice]);

  const STATUS_COLOR: Record<string, string> = { online: 'text-emerald-300', offline: 'text-white/30', binding: 'text-amber-300' };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
            <Watch size={15} /> {t?.wearableTitle || '穿戴设备'}
          </div>
          <button type="button" onClick={() => void handleBind()} className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1.5 text-[11px] font-bold text-cyan-200 hover:bg-cyan-400/20">
            <Plus size={12} /> 绑定设备
          </button>
        </div>
        {msg && <div className="mt-2 rounded-md border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] text-cyan-200">{msg}</div>}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 custom-scrollbar">
        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-white/40">设备 ({devices.length})</div>
          {loading && <div className="text-xs text-white/30">加载中...</div>}
          {devices.length === 0 && !loading && <div className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-xs text-white/35">暂无设备，点右上角绑定</div>}
          {devices.map(d => (
            <div key={d.id} className="rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Radio size={12} className={STATUS_COLOR[d.status] || 'text-white/30'} />
                  <span className="text-xs font-bold text-white/80">{d.name}</span>
                  <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-white/40">{d.platform}</span>
                </div>
                <button type="button" onClick={() => void handleUnbind(d.id)} className="rounded p-1 text-white/35 hover:bg-red-500/10 hover:text-red-300"><Trash2 size={12} /></button>
              </div>
              <div className="mt-1 text-[10px] text-white/30">
                ID: {d.id} | 绑定码: {d.bindCode} | 能力: {d.capabilities?.join(', ')} | 最后在线: {d.lastSeen?.slice(0, 19)}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-white/40">模拟设备输入</div>
          <div className="flex items-center gap-2">
            <input value={simVoice} onChange={e => setSimVoice(e.target.value)} placeholder="语音输入（如：今天天气怎么样）" className="flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
            <button type="button" onClick={() => void handleSimulate()} className="inline-flex items-center gap-1 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1.5 text-[11px] font-bold text-emerald-200 hover:bg-emerald-400/20">
              <Send size={12} /> 发送
            </button>
          </div>
          {simResult && (
            <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/60">
              <div className="font-bold text-white/70">上下文 Prompt:</div>
              <pre className="mt-1 whitespace-pre-wrap text-[10px] text-white/40">{simResult.contextPrompt}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
